import React, { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { Badge, Button, Card, DisabledText, ExternalLinkIcon, Inline, Search } from "@KristianIV/common-ui";
import { goToAdminCommanderByUserId, goToCommanderByUserId, goToReplayByUserId } from "../login/LoginByEmail";
import { SearchEntity, UserSearchResult, searchForUserAndOrganisation } from "../services/adminService";
import styles from "./Dashboard.module.css";

const SEARCH_DEBOUNCE_MS = 1000;

/** Finds a user or organisation by name and offers the logins an admin needs for it. */
const GlobalSearch = ({ jwtToken }: { jwtToken: string }) => {
  const history = useHistory();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const onSearch = useCallback(
    (value: string) => {
      setTerm(value);
      setSelectedId(undefined);
      if (value === "") {
        setResults([]);
        setOpen(false);
        return;
      }
      setLoading(true);
      setOpen(true);
      searchForUserAndOrganisation(jwtToken, value)
        .then(setResults)
        .catch((error) => {
          setResults([]);
          alert(`Failed to search for user ${value}: ${error.message}`);
        })
        .finally(() => setLoading(false));
    },
    [jwtToken]
  );

  return (
    <div
      className={styles.search}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setOpen(false);
        }
      }}
    >
      <Search placeholder="Search for user or organisation" debounceMs={SEARCH_DEBOUNCE_MS} onSearch={onSearch} />
      {open && (
        <>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <Card interactive={false} className={styles.results}>
            {loading && <DisabledText>Searching...</DisabledText>}
            {!loading && results.length === 0 && <DisabledText>No results for "{term}"</DisabledText>}
            {results.map((item) => {
              const isOrganisation = item.entity === SearchEntity.organisation;
              const selected = selectedId === item.id;
              return (
                <div
                  key={`${item.entity}-${item.id}`}
                  className={`${styles.result} ${selected ? styles.resultSelected : ""}`}
                  onClick={() => setSelectedId(item.id)}
                >
                  <Inline justify="between" wrap={false} gap="md">
                    <span className={styles.resultName}>{item.name}</span>
                    <Badge color={isOrganisation ? "blue" : undefined}>{isOrganisation ? "Organisation" : "User"}</Badge>
                  </Inline>
                  {selected && (
                    <Inline wrap={false} className={styles.resultActions}>
                      {isOrganisation && (
                        <Button
                          size="small"
                          onClick={() =>
                            history.push({ pathname: `/organisation/${item.id}`, state: { username: item.name } })
                          }
                        >
                          Dashboard
                        </Button>
                      )}
                      <Button size="small" icon={<ExternalLinkIcon />} onClick={() => goToAdminCommanderByUserId(item.ownerId, jwtToken)}>
                        Admin
                      </Button>
                      <Button size="small" icon={<ExternalLinkIcon />} onClick={() => goToCommanderByUserId(item.ownerId, jwtToken)}>
                        Public
                      </Button>
                      <Button size="small" icon={<ExternalLinkIcon />} onClick={() => goToReplayByUserId(item.ownerId, jwtToken)}>
                        Replay
                      </Button>
                    </Inline>
                  )}
                </div>
              );
            })}
            {!loading && results.length > 0 && (
              <div className={styles.resultsFooter}>
                {results.length} {results.length === 1 ? "result" : "results"} · Esc closes
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default GlobalSearch;
